import { configureHttp, getJson, postJson } from '@unified-dashboard/http';
import { getConfig } from './config.js';
import { AuthBootstrapError } from './errors.js';
import { getAccessToken } from './selectors.js';
import { clearState, setAccessToken, setMe } from './state.js';
let bootstrapPromise = null;
/**
 * Single refresh round-trip. Updates the in-memory token on success.
 *
 * Plays two roles:
 *   1. The initial token acquisition during `bootstrap()`.
 *   2. The `onUnauthorized` callback wired into `@unified-dashboard/http`,
 *      called once per gateway 401 to recover.
 *
 * On any failure (401, 5xx, network) the local state is cleared and `false`
 * is returned. This function does NOT redirect — that decision belongs to
 * the caller (shell renders a LoginScreen; `login()` performs the actual
 * navigation when the user clicks the button).
 */
export async function doRefresh() {
    const config = getConfig();
    try {
        const { accessToken } = await postJson(config.authRefreshPath, {}, { skipAuth: true, retry: false });
        setAccessToken(accessToken);
        return true;
    }
    catch {
        clearState();
        return false;
    }
}
/**
 * Boot the auth subsystem. Idempotent: concurrent calls return the same
 * Promise; a second call after settle returns the cached Promise (which is
 * already resolved or rejected).
 *
 * 1. Wires `@unified-dashboard/http` via `configureHttp` — the rest of the
 *    platform depends on this happening before any authenticated request.
 * 2. Calls `doRefresh` to obtain the first access token.
 *    - On success → continues to step 3.
 *    - On failure → leaves the state cleared (user=null, accessToken=null)
 *      and RESOLVES the Promise. The shell observes `user === null` and
 *      renders its LoginScreen. There is intentionally no `unauthenticated`
 *      throw: anonymous is a valid, expected state on cold load.
 * 3. Calls `GET /v1/me` and stores `user` + `allowedApps`.
 *
 * Only failure mode that throws:
 *   - `service-error`: `/v1/me` failed *after* a successful refresh. The
 *     user IS authenticated but the catalog endpoint is broken — that's
 *     exceptional and should surface to the user as an error overlay.
 */
export function bootstrap() {
    if (bootstrapPromise)
        return bootstrapPromise;
    bootstrapPromise = (async () => {
        configureHttp({
            getAccessToken,
            onUnauthorized: doRefresh,
        });
        const refreshed = await doRefresh();
        if (!refreshed) {
            // No session — the shell will render its LoginScreen based on
            // `user === null`. Resolve normally; this is not an error.
            return;
        }
        let me;
        try {
            me = await getJson(getConfig().meEndpoint);
        }
        catch (err) {
            throw new AuthBootstrapError('service-error', '/v1/me failed', err);
        }
        setMe(me.user, me.allowedApps);
    })();
    return bootstrapPromise;
}
/** @internal — for tests. */
export function _resetBootstrap() {
    bootstrapPromise = null;
}
//# sourceMappingURL=orchestration.js.map